import { useState } from 'react';
import { Box, Button, Typography, Grid, IconButton } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import NetworkLogo from '../ui-components/network-logo.component';
import { useUserProfile } from '../../hooks/useUserProfile';
import { ColorType } from '../../assets/colors/colors.g';
import { styles } from '../../styles/styles.util';
import { useSelector } from 'react-redux';
import { getTheme } from '../../stores/selectors/theme.selector';
import { SimpleHeader } from '../ui-components/simple-header.component';
import { useAuth } from '../../hooks/useAuth';
import { PhoneNumberField } from '../ui-components/phone-number-field.component';
import OtpVerification from '../user/otp-verification/otp-verification.component';
import { PhoneNumberAlreadyExistsError } from '../../errors/phone-number-already-exists';
import { showSnackBar } from '../../util/toast.utility';
import PhoneSmsAlertIcon from '../../assets/icons/phoneSmsAlert.svg';
import { arrowBack } from '../../assets/icons/icons';
import { LoadingDots } from '../ui-components/loading-dots/loading-dots.component';
import { useLogger } from '../../services/logging/logger-context';

/**
 * AddPhone Component
 *
 * Displays a mandatory phone number addition/verification screen for drivers.
 * Users must add or verify their phone number before continuing to use the application.
 * Features:
 * - Phone number input with validation via PhoneNumberField component
 * - Sends OTP verification code via SMS through updateUserPhone API through useUserProfile hook
 * - Two-step flow: phone entry → OTP verification
 * - Handles phone number already in use errors
 * - Shows SMS alerts information for idle fee notifications
 * - Provides logout option in header via useAuth hook
 * - Supports resending verification codes
 */
export const AddPhone = () => {
  const { t } = useTranslation();
  const { updateUserPhone, verifyPhoneCode, hasPhoneNumber, getPhoneNumber } = useUserProfile();
  const theme = useSelector(getTheme);
  const [isLoading, setIsLoading] = useState(false);
  const auth = useAuth();
  const location = useLocation();
  const { logger } = useLogger();
  const [phone, setPhone] = useState(hasPhoneNumber() ? getPhoneNumber() : '');
  const [phoneNormalized, setPhoneNormalized] = useState(hasPhoneNumber() ? getPhoneNumber() : '');
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [verifyOtp, setVerifyOtp] = useState(false);
  const [otpError, setOtpError] = useState<boolean>(false);
  const [phoneNumberInUseError, setPhoneNumberInUseError] = useState<boolean>(false);

  const handleSubmit = async () => {
    if (!isPhoneValid || !phoneNormalized) {
      return;
    }

    try {
      setIsLoading(true);
      await updateUserPhone(phoneNormalized);
      setVerifyOtp(true);
      setOtpError(false);
    } catch (error) {
      logger.error('Failed to add phone number', error);
      showSnackBar(t('unknown_error'));
    } finally {
      setIsLoading(false);
    }
  };

  const verifyPhoneValidationCode = async (code: string) => {
    try {
      await verifyPhoneCode(code);
    } catch (error) {
      if (error instanceof PhoneNumberAlreadyExistsError) {
        setOtpError(false);
        setVerifyOtp(false);
        setPhoneNumberInUseError(true);
        logger.error('Failed to add phone number because it is already in use', error);
      } else {
        setOtpError(true);
      }
    }
  };

  const resendPhoneCode = async () => {
    try {
      await updateUserPhone(phoneNormalized);
    } catch (error) {
      logger.error('Failed to resend phone code', error);
      showSnackBar(t('unknown_error'));
    }
  };

  const handleSignOut = () => {
    auth.signOut(location.pathname);
  };

  if (verifyOtp) {
    return (
      <div style={styles.container}>
        <div style={styles.wrapper}>
          <SimpleHeader
            left={
              <IconButton
                onClick={() => setVerifyOtp(false)}
                style={{ padding: 0, backgroundColor: 'transparent' }}
                aria-label={t('back_to_previous_page')}
              >
                {arrowBack(theme.primary)}
              </IconButton>
            }
            right={<NetworkLogo height='28px' width='auto' maxWidth={128} />}
          />
          <Typography
            variant='h3'
            sx={{
              fontFamily: 'Inter',
              fontSize: '24px',
              fontWeight: 600,
              lineHeight: '32px',
              textAlign: 'center',
              color: ColorType.BLACK,
              padding: '24px 24px 0',
            }}
          >
            {t('enter_verification_code')}
          </Typography>
          <OtpVerification
            otpLength={6}
            error={otpError}
            setError={setOtpError}
            verifyUser={phoneNormalized}
            handleVerifyOtp={verifyPhoneValidationCode}
            sendNewCode={resendPhoneCode}
            description={'we_have_sent_code'}
            description1={t('enter_code_to_verify_phone')}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.wrapper}>
        <SimpleHeader
          left={
            <Typography
              onClick={handleSignOut}
              sx={{
                fontFamily: 'Inter',
                fontSize: '16px',
                fontWeight: 600,
                lineHeight: '24px',
                color: ColorType.GREY6,
                cursor: 'pointer',
              }}
            >
              {t('logout')}
            </Typography>
          }
          right={<NetworkLogo height='28px' width='auto' maxWidth={128} />}
        />

        <Box
          sx={{
            padding: '24px 24px',
            height: '100%',
          }}
        >
          {/* Header */}
          <Typography
            variant='h3'
            sx={{
              fontFamily: 'Inter',
              fontSize: '24px',
              fontWeight: 600,
              lineHeight: '32px',
              textAlign: 'center',
              color: ColorType.BLACK,
              mt: 2,
              mb: 2,
            }}
          >
            {hasPhoneNumber() ? t('verify_your_phone_number') : t('add_a_phone_number')}
          </Typography>

          {/* Phone Input */}
          <Grid container sx={{ mb: 4, gap: 1 }}>
            <PhoneNumberField
              value={phone}
              onChange={(newPhone) => {
                setPhone(newPhone);
                setPhoneNumberInUseError(false);
              }}
              required={true}
              onValidationChange={(isValid, normalized) => {
                setIsPhoneValid(isValid);
                setPhoneNormalized(normalized);
              }}
              inputProps={{
                'aria-label': t('canada_selected'),
              }}
            />
            {/* If the phone number is in use, show the error message 
            else show the send a pin to login description text */}
            {phoneNumberInUseError ? (
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '20px',
                  color: ColorType.NEGATIVE,
                }}
              >
                {t('phone_number_is_taken')}
              </Typography>
            ) : (
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '20px',
                  color: ColorType.GREY5,
                }}
              >
                {t('send_a_pin_to_login')}
              </Typography>
            )}
          </Grid>

          {/* SMS Alerts Section */}
          <Box sx={{ mb: 4, gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box
                sx={{
                  minWidth: '24px',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={PhoneSmsAlertIcon}
                  alt=''
                  style={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: theme.navigationSelectedColor,
                    borderRadius: '50%',
                    padding: '4px',
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: ColorType.BLACK,
                    mb: 1,
                  }}
                >
                  {t('idle_fee_sms_alerts_header')}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '20px',
                    color: ColorType.GREY6,
                  }}
                >
                  {t('idle_fee_sms_alerts_description')}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Submit button */}
          <Button
            variant='contained'
            onClick={handleSubmit}
            disabled={isLoading || !isPhoneValid || !phoneNormalized}
            fullWidth
            sx={{
              backgroundColor: theme.colorPalettes.primary,
              color: ColorType.WHITE,
              fontSize: '16px',
              fontWeight: 600,
              height: '48px',
              borderRadius: '4px',
              textTransform: 'none',
              fontFamily: 'Inter',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: theme.colorPalettes.primary,
                boxShadow: 'none',
              },
              '&:disabled': {
                backgroundColor: ColorType.GREY0,
                color: ColorType.GREY3,
              },
            }}
          >
            {isLoading ? <LoadingDots /> : t('next')}
          </Button>
        </Box>
      </div>
    </div>
  );
};
